import type {
  BaseAnalyticsFilter,
  WellnessFilter,
} from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useAnalyticsQuery } from './useAnalyticsQuery';

export function serializeFilters(filters: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (value instanceof Date) {
        const year = value.getUTCFullYear();
        const month = String(value.getUTCMonth() + 1).padStart(2, '0');
        const day = String(value.getUTCDate()).padStart(2, '0');
        params.append(key, `${year}-${month}-${day}`);
      } else if (Array.isArray(value) && value.length > 0) {
        params.append(key, value.join(','));
      } else {
        params.append(key, String(value));
      }
    }
  });

  return params;
}

export function serializeAnalyticsFilters(filters: BaseAnalyticsFilter): URLSearchParams {
  return serializeFilters(filters as Record<string, unknown>);
}

export function serializeWellnessFilters(filters: WellnessFilter): URLSearchParams {
  return serializeFilters(filters as Record<string, unknown>);
}

export function normalizeAnalyticsFilters(
  filters: BaseAnalyticsFilter | undefined,
  defaults: BaseAnalyticsFilter,
): BaseAnalyticsFilter {
  return {
    ...defaults,
    ...filters,
  };
}

export function createDefaultBaseFilters(): BaseAnalyticsFilter {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 12);
  startDate.setHours(0, 0, 0, 0);

  return {
    startDate,
    endDate,
    experienceIds: [],
    unitIds: [],
    granularity: 'month',
  };
}

export function createUserParams(users: Array<{ firstName: string; lastName: string }>): string {
  return users
    .filter(user => user.firstName?.trim() && user.lastName?.trim())
    .map(user => `${user.firstName.trim()}|${user.lastName.trim()}`)
    .sort()
    .join(',');
}

export interface ParallelQueryConfig {
  baseUrl: string;
  endpoints: Array<{
    key: string;
    path: string;
    additionalParams?: Record<string, string>;
  }>;
  filters: BaseAnalyticsFilter;
  queryKeyPrefix: string;
  queryOptions?: {
    staleTime?: number;
    gcTime?: number;
  };
}

export async function fetchAnalyticsData<T = unknown>(
  url: string,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(url, {
    signal,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`API Error: ${response.status} ${errorText}`);
  }

  return response.json();
}

export function createAnalyticsUrl(
  baseUrl: string,
  endpoint: string,
  filters: BaseAnalyticsFilter,
  additionalParams?: Record<string, string>,
): string {
  const params = serializeAnalyticsFilters(filters);

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.append(key, value);
    });
  }

  return `${baseUrl}${endpoint}?${params.toString()}`;
}

export function createParallelQueries(config: ParallelQueryConfig) {
  return config.endpoints.map(endpoint => ({
    queryKey: [
      config.queryKeyPrefix,
      endpoint.path,
      config.filters,
      ...(endpoint.additionalParams ? [endpoint.additionalParams] : []),
    ],
    queryFn: () =>
      fetchAnalyticsData(
        createAnalyticsUrl(
          config.baseUrl,
          endpoint.path,
          config.filters,
          endpoint.additionalParams,
        ),
      ),
    staleTime: config.queryOptions?.staleTime,
    gcTime: config.queryOptions?.gcTime,
  }));
}

export function formatSessionMetrics(data: { totalMinutes: number; totalSessions: number }) {
  const { totalMinutes, totalSessions } = data;
  return {
    averageSessionLength: totalSessions > 0 ? Math.round(totalMinutes / totalSessions) : 0,
    formattedDuration:
      totalMinutes > 60
        ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`
        : `${totalMinutes}m`,
  };
}

export interface AnalyticsHookConfig {
  baseUrl: string;
  queryKeyPrefix: string;
}

export function createAnalyticsHook(config: AnalyticsHookConfig) {
  return <T>(endpoint: string, filters?: BaseAnalyticsFilter) => {
    const normalizedFilters = normalizeAnalyticsFilters(filters, createDefaultBaseFilters());
    return useAnalyticsQuery<T>(endpoint, normalizedFilters, config);
  };
}
